import { queryOptions, useQuery } from '@tanstack/react-query'

import { pricingKeys } from './keys'

import { QueryConfig } from '@/lib/react-query'
import { supabase } from '@/lib/supabase-client'
import { Database } from '@/lib/types/supabase'
import { logger } from '@/logger'
import { useAuthenticatedUser } from '@/state/session'

export type UserSubscription = Database['public']['Tables']['user_subscriptions']['Row']

type GetUserSubscriptionOptions = {
  userId: string
}

export const getUserSubscription = async ({ userId }: GetUserSubscriptionOptions): Promise<UserSubscription | null> => {
  const { data, error } = await supabase
    .from('user_subscriptions')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle()

  if (error) {
    logger.error('Error fetching user subscription', { error, userId })
    throw error
  }

  return data
}

export const getUserSubscriptionQueryOptions = ({ userId }: GetUserSubscriptionOptions) =>
  queryOptions({
    queryKey: pricingKeys.userSubscription(userId),
    queryFn: () => getUserSubscription({ userId }),
    enabled: Boolean(userId),
  })

type UseUserSubscriptionOptions = {
  userId?: string
  queryConfig?: QueryConfig<typeof getUserSubscriptionQueryOptions>
}

export const useUserSubscription = ({ userId, queryConfig }: UseUserSubscriptionOptions = {}) => {
  const authenticatedUser = useAuthenticatedUser()
  const effectiveUserId = userId ?? authenticatedUser.supaUser.id

  return useQuery({
    ...getUserSubscriptionQueryOptions({ userId: effectiveUserId }),
    ...(queryConfig || {}),
  })
}
